import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown, faSpinnerThird } from "@fortawesome/sharp-thin-svg-icons"
import { SxProps } from "@mui/material"
import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import React, { useCallback, useEffect, useRef, useState } from "react"
import {
    buildAutocompleteSx,
    makeRenderOption,
    Option,
    renderChips,
} from "../MuiAutocomplete/shared"

/**
 * Trigger returned by an RTK Query `useLazy<Name>Query` hook.
 * The caller owns the hook (rules of hooks) and passes the trigger down.
 */
type LazyTrigger<TArg, TRes> = (arg: TArg) => { unwrap: () => Promise<TRes> }

type Props<TArg, TRes> = {
    /** The trigger from a `useLazy<Name>Query()` call. */
    trigger: LazyTrigger<TArg, TRes>
    /** Build the request payload for a given page + search term. */
    buildQueryArg: (params: { page: number; page_size: number; search: string }) => TArg
    /**
     * Map the endpoint response into options + whether more pages exist.
     * For `have_more` endpoints return `hasMore: res.have_more`; for `total_pages`
     * endpoints return `hasMore: page < res.total_pages`.
     */
    selectData: (res: TRes, page: number) => { options: Option[]; hasMore: boolean }
    pageSize?: number
    debounceMs?: number
    /** Distance in px from the bottom of the list that triggers the next page fetch. */
    scrollThreshold?: number

    value?: Option | Option[] | null
    onChange?: (value: Option | Option[] | null) => void
    multiple?: boolean

    label?: string
    placeholder?: string
    disabled?: boolean
    error?: boolean
    helperText?: string
    required?: boolean
    fullWidth?: boolean
    size?: "small" | "medium" | "large"
    disablePortal?: boolean
    containerHeight?: string
    sx?: SxProps
    disableClearable?: boolean
    placement?: "top" | "bottom" | "top-start" | "top-end" | "bottom-start" | "bottom-end"
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: Option,
        { selected }: { selected: boolean }
    ) => React.ReactNode
}

const dedupeById = (opts: Option[]): Option[] => {
    const seen = new Set<string | number>()
    const out: Option[] = []
    for (const o of opts) {
        if (seen.has(o.id)) continue
        seen.add(o.id)
        out.push(o)
    }
    return out
}

function MuiAsyncAutoComplete<TArg, TRes>({
    trigger,
    buildQueryArg,
    selectData,
    pageSize = 20,
    debounceMs = 300,
    scrollThreshold = 60,
    value,
    onChange,
    multiple = false,
    label,
    placeholder,
    disabled = false,
    error = false,
    helperText,
    required = false,
    fullWidth = false,
    size = "medium",
    disablePortal = true,
    containerHeight,
    sx = {},
    disableClearable = false,
    placement,
    renderOption,
}: Props<TArg, TRes>) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState("")
    const [options, setOptions] = useState<Option[]>([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)

    // Keep latest values available to the (otherwise stale) scroll handler closure.
    const stateRef = useRef({ page, hasMore, loading, loadingMore, search })
    stateRef.current = { page, hasMore, loading, loadingMore, search }

    // Monotonic token bumped on every fresh (page-1) fetch. Responses from an
    // older search/reset are discarded so a slow request can't clobber a newer one.
    const reqIdRef = useRef(0)

    const fetchPage = useCallback(
        async (pageToFetch: number, searchText: string, append: boolean) => {
            // A fresh fetch (new search / first page) invalidates everything in flight.
            const reqId = append ? reqIdRef.current : ++reqIdRef.current
            if (append) setLoadingMore(true)
            else setLoading(true)
            try {
                const res = await trigger(
                    buildQueryArg({ page: pageToFetch, page_size: pageSize, search: searchText })
                ).unwrap()
                if (reqId !== reqIdRef.current) return
                const { options: newOptions, hasMore: more } = selectData(res, pageToFetch)
                setOptions(prev => (append ? dedupeById([...prev, ...newOptions]) : newOptions))
                setHasMore(!!more)
                setPage(pageToFetch)
            } catch {
                if (reqId !== reqIdRef.current) return
                if (!append) {
                    setOptions([])
                    setHasMore(false)
                    setPage(1)
                }
            } finally {
                // Appends only ever run one at a time, so always release them. For a
                // fresh fetch, only release if a newer one hasn't superseded it.
                if (append) setLoadingMore(false)
                else if (reqId === reqIdRef.current) setLoading(false)
            }
        },
        [trigger, buildQueryArg, selectData, pageSize]
    )

    // Load the first page when the menu opens.
    const handleOpen = () => {
        setOpen(true)
        fetchPage(1, search, false)
    }

    // Debounced re-fetch of page 1 while typing (server-side search).
    useEffect(() => {
        if (!open) return
        const t = setTimeout(() => fetchPage(1, search, false), debounceMs)
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search])

    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const el = event.currentTarget
        const s = stateRef.current
        const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight < scrollThreshold
        if (reachedBottom && !s.loading && !s.loadingMore && s.hasMore) {
            fetchPage(s.page + 1, s.search, true)
        }
    }

    // Merge selected values that aren't on the loaded pages so MUI doesn't warn
    // about out-of-range values and chips/labels always resolve.
    const selectedArray: Option[] = multiple
        ? ((value as Option[]) ?? [])
        : value
          ? [value as Option]
          : []
    const missingSelected = selectedArray.filter(s => !options.some(o => o.id === s.id))
    const mergedOptions = [...options, ...missingSelected]

    const isLoading = loading || loadingMore

    return (
        <Autocomplete
            multiple={multiple}
            disablePortal={disablePortal}
            disableCloseOnSelect={multiple}
            open={open}
            onOpen={handleOpen}
            onClose={() => {
                setOpen(false)
                // Drop the typed term so the next open starts from an unfiltered page 1
                // and the stale search isn't replayed in the request payload.
                setSearch("")
            }}
            options={mergedOptions}
            value={value}
            loading={loading}
            getOptionDisabled={(option: any) => !!option.disabled}
            slotProps={{
                ...(placement ? { popper: { placement } } : {}),
                listbox: { onScroll: handleScroll },
            }}
            popupIcon={
                isLoading ? <FontAwesomeIcon icon={faSpinnerThird} spinPulse /> : <FontAwesomeIcon icon={faAngleDown} />
            }
            disableClearable={disableClearable}
            onChange={(_, newValue) => {
                if (!multiple) {
                    onChange?.(newValue as Option)
                    return
                }
                onChange?.(newValue as Option[])
            }}
            onInputChange={(_, newInput, reason) => {
                // Display text is left uncontrolled so MUI keeps showing the selected label in
                // single-select mode. We only capture typed input to drive server-side search.
                if (reason !== "input" && reason !== "clear") return
                // Reset pagination immediately (before the debounced fetch) so a scroll during
                // the debounce window can't append the next page of the previous search.
                setPage(1)
                setHasMore(false)
                setSearch(reason === "input" ? newInput : "")
            }}
            getOptionLabel={(option: any) => option.title || ""}
            renderOption={
                (renderOption ??
                    makeRenderOption({
                        multiple,
                        showSelectAll: false,
                        isAllSelected: false,
                        isIndeterminate: false,
                    })) as any
            }
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            disabled={disabled}
            fullWidth={fullWidth}
            // Server-side search: never filter the loaded page client-side.
            filterOptions={(x: any) => x}
            limitTags={multiple ? 1 : undefined}
            size={size === "large" ? "medium" : size}
            renderValue={multiple ? renderChips : undefined}
            sx={buildAutocompleteSx({ error, disabled, containerHeight, size, sx, multiple })}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    helperText={helperText}
                    required={required}
                    variant="standard"
                />
            )}
        />
    )
}

export type { Option }
export default MuiAsyncAutoComplete
